import {
  SvgArchive,
  SvgCopy,
  SvgDownload,
  SvgExternalLink,
  SvgFolder,
  SvgLink,
  SvgLinkOff,
  SvgNotes,
  SvgPin,
  SvgPinFill,
  SvgPublicOn,
  SvgShare,
  SvgStar,
  SvgStarOutline,
  SvgTrash,
  SvgUnarchive,
  Tooltip,
} from '@loomhq/lens';
import copy from 'copy-to-clipboard';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import { updateShowMoveModal } from 'actions/moveModal';
import { updateShareVideo } from 'actions/shareVideo';
import { LARGE_TABLET_MIN_WIDTH } from 'constants/breakpoints';
import { isDev } from 'constants/environment';
import { DUPLICATE_ITEM_CLICK, ORGANIZE_VIDEO } from 'constants/events';
import { STORE_FILLER_WORD_RANGES } from 'constants/featureFlag';
import {
  ARCHIVE,
  COPY_LINK,
  DELETE,
  DIVE,
  DOWNLOAD,
  DUPLICATE,
  MAKE_PRIVATE,
  MAKE_PUBLIC,
  MOVE,
  PIN_HELPER_TEXT,
  PIN_TO_PROFILE,
  REVIEW_TRANSCRIPT,
  SHARE,
  STAR,
  TRANSCRIPT,
  UNARCHIVE,
  UNPIN_FROM_PROFILE,
  UNSTAR,
} from 'constants/menuOptions';
import { SHARE_PAGE } from 'constants/menuPages';
import {
  CONFIRM_DELETE_ITEM_MODAL,
  DESTINATION_MOVE_MODAL,
  DOWNLOAD_VIDEO_INFO_MODAL,
  SHARE_UI_MODAL,
} from 'constants/modalTypes';
import { LOOM_BARE_URI, MY_VIDEOS_PAGE } from 'constants/routes';
import {
  AUTHENTICATED_USER_ACCESS,
  FAVORITES,
  PERSONAL_ARCHIVE_WRITE,
  PERSONAL_LIBRARY_WRITE,
  SHARED_LIBRARY_WRITE,
  TEAM_LIBRARY_WRITE,
  VIDEO_SHARE_PRIVATE_ACTION,
  VIDEO_TOOLS_ACCESS,
} from 'constants/scopes';
import { SHOW_DOWNLOAD_VIDEO_INFO_MODAL } from 'constants/triggers';
import { ADMIN } from 'constants/userRoles';
import {
  FAILED,
  SHORT_DURATION,
  TRANSCRIPTION_STATUSES,
} from 'constants/videoTranscript';
import { VideoVisibility as VisibilityType } from 'constants/visibility';
import { startDownload } from 'creators/header';
import { updateUserPinnedVideo } from 'creators/pinnedVideo';
import {
  archiveVideoCreator,
  fetchShareVideoDownloadUrlCreator,
  handleToggleTranscriptCreator,
} from 'creators/shareVideo';
import { completeTriggerCreator } from 'creators/triggers';
import { duplicateVideoCreator } from 'creators/video';
import { toggleVideoFavoriteCreator } from 'creators/videosDashboard';
import * as analytics from 'utilities/analytics';
import { isMobile } from 'utilities/device';
import { TrimProgressTooltip } from 'utilities/trim-progress-tooltip';

import { useFlagIsActivated } from 'hooks/featureFlag';
import { useShouldShowTrigger } from 'hooks/triggers';
import { useHasScope } from 'hooks/useHasScopes';
import { useUserInSameWorkspaceAsItem } from 'hooks/workspace';

import useDestination from './useDestination';
import { useMatchMedia } from './useMatchMedia';

// Used in JSDoc type hinting
// eslint-disable-next-line no-unused-vars
import { Video as MoveVideoType } from 'components/destination-move-modal/types';

const commonProps = optionType => {
  switch (optionType) {
    case COPY_LINK:
      return {
        key: COPY_LINK,
        icon: <SvgLink />,
        title: COPY_LINK,
      };
    case PIN_TO_PROFILE:
      return {
        key: PIN_TO_PROFILE,
        icon: <SvgPin />,
        title: PIN_TO_PROFILE,
      };
    case UNPIN_FROM_PROFILE:
      return {
        key: UNPIN_FROM_PROFILE,
        icon: <SvgPinFill />,
        title: UNPIN_FROM_PROFILE,
      };
    case SHARE:
      return {
        key: SHARE,
        icon: <SvgShare />,
        title: SHARE,
      };
    case REVIEW_TRANSCRIPT:
      return {
        key: REVIEW_TRANSCRIPT,
        icon: <SvgNotes />,
        title: REVIEW_TRANSCRIPT,
      };
    case TRANSCRIPT:
      return {
        key: TRANSCRIPT,
        icon: <SvgNotes />,
        title: TRANSCRIPT,
      };
    case MOVE:
      return {
        key: MOVE,
        icon: <SvgFolder />,
        title: MOVE,
      };
    case STAR:
      return {
        key: STAR,
        icon: <SvgStarOutline />,
        title: STAR,
      };
    case UNSTAR:
      return {
        key: UNSTAR,
        icon: <SvgStar />,
        title: UNSTAR,
      };
    case DUPLICATE:
      return {
        key: DUPLICATE,
        icon: <SvgCopy />,
        title: DUPLICATE,
      };
    case ARCHIVE:
      return {
        key: ARCHIVE,
        icon: <SvgArchive />,
        title: ARCHIVE,
      };
    case UNARCHIVE:
      return {
        key: UNARCHIVE,
        icon: <SvgUnarchive />,
        title: UNARCHIVE,
      };
    case DOWNLOAD:
      return {
        key: DOWNLOAD,
        icon: <SvgDownload />,
        title: DOWNLOAD,
      };
    case DELETE:
      return {
        key: DELETE,
        icon: <SvgTrash />,
        title: DELETE,
      };
    case MAKE_PUBLIC:
      return {
        key: MAKE_PUBLIC,
        icon: <SvgPublicOn />,
        title: MAKE_PUBLIC,
      };
    case MAKE_PRIVATE:
      return {
        key: MAKE_PRIVATE,
        icon: <SvgLinkOff />,
        title: MAKE_PRIVATE,
      };
    case DIVE:
      return {
        key: DIVE,
        icon: <SvgExternalLink />,
        title: DIVE,
      };
    default: {
      return {};
    }
  }
};

const SHARE_VIDEO_MENU = [
  COPY_LINK,
  PIN_TO_PROFILE,
  UNPIN_FROM_PROFILE,
  SHARE,
  REVIEW_TRANSCRIPT,
  TRANSCRIPT,
  MOVE,
  STAR,
  UNSTAR,
  DUPLICATE,
  ARCHIVE,
  UNARCHIVE,
  DOWNLOAD,
  DELETE,
  DIVE,
];

const checkConditionsMet = conditions => conditions.every(c => c);

const getVideoActionMenuItems = ({
  video,
  scopes,
  flags,
  permissions,
  dispatch,
  extras,
}) => {
  const {
    userIsOwner,
    userCanEdit,
    userInSameWorkspace,
    userCanAccessTranscript,
    userIsLoomAdmin,
  } = permissions;

  const getVideoOptionOrNull = menuItem => {
    switch (menuItem) {
      case COPY_LINK: {
        if (!checkConditionsMet([!video.archived])) {
          return null;
        }

        return {
          ...commonProps(COPY_LINK),
          onClick: () => {
            copy(`https://${LOOM_BARE_URI}/share/${video.id}`, {
              format: 'text/plain',
            });
          },
        };
      }

      case SHARE: {
        if (
          !checkConditionsMet([!video.archived, scopes.hasAuthenticatedAccess])
        ) {
          return null;
        }

        return {
          ...commonProps(SHARE),
          onClick: () => {
            dispatch(openModal(SHARE_UI_MODAL, { video }));
          },
        };
      }

      case REVIEW_TRANSCRIPT: {
        if (
          !checkConditionsMet([
            userCanEdit,
            isMobile || !extras.videoButtonsShowing,
            video.active_video_transcript_id,
            userCanAccessTranscript,
            !extras.isTrimMode,
            flags.isInFillerWordRemovalFlag,
          ])
        ) {
          return null;
        }

        const shouldDisableTranscript =
          !video.active_video_transcript_id ||
          extras.transcriptStatus === FAILED ||
          extras.transcriptStatus === SHORT_DURATION ||
          !TRANSCRIPTION_STATUSES.includes(extras.transcriptStatus);

        return {
          ...commonProps(REVIEW_TRANSCRIPT),
          disabled: shouldDisableTranscript,
          onClick: () => {
            dispatch(handleToggleTranscriptCreator());
          },
        };
      }

      case TRANSCRIPT: {
        if (
          !checkConditionsMet([
            !userCanEdit,
            isMobile || !extras.videoButtonsShowing,
            video.active_video_transcript_id,
            userCanAccessTranscript,
            !extras.isTrimMode,
            !flags.isInFillerWordRemovalFlag,
          ])
        ) {
          return null;
        }

        const shouldDisableTranscript =
          !video.active_video_transcript_id ||
          extras.transcriptStatus === FAILED ||
          extras.transcriptStatus === SHORT_DURATION ||
          !TRANSCRIPTION_STATUSES.includes(extras.transcriptStatus);

        return {
          ...commonProps(TRANSCRIPT),
          disabled: shouldDisableTranscript,
          onClick: () => {
            dispatch(handleToggleTranscriptCreator());
          },
        };
      }

      case MOVE: {
        if (
          !checkConditionsMet([
            userIsOwner || userInSameWorkspace,
            !video.archived,
            scopes.hasVideoToolsAccessScope,
            scopes.hasAuthenticatedAccess,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(MOVE),
          onClick: () => {
            if (flags.destinationRolloutHubFlag) {
              // Destination modal expects items in the GraphQL format returned by the getLooms query on the Destination pages,
              // so this transforms the data from the share page state to the format that's expected
              const folder = video.folders
                ? video.folders[video.folders.length - 1]
                : {};

              /** @type {MoveVideoType} */
              const moveVideo = { ...video, folder };

              dispatch(
                openModal(DESTINATION_MOVE_MODAL, { videos: [moveVideo] })
              );
            } else {
              video.type = 'video';
              dispatch(updateShowMoveModal(true, [video]));
            }

            analytics.track(ORGANIZE_VIDEO, {
              root_folder: video.folder_id,
              is_owner: video.current_user_is_owner,
            });
          },
        };
      }

      case STAR: {
        if (
          !checkConditionsMet([
            userInSameWorkspace,
            !video.isUserFavorite,
            !video.needs_password,
            !video.archived,
            scopes.hasFavoritesScope,
            scopes.hasAuthenticatedAccess,
            !flags.destinationRolloutHubFlag,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(STAR),
          onClick: () => {
            dispatch(toggleVideoFavoriteCreator(video.id, true, video));
            dispatch(
              updateShareVideo({
                ...video,
                isUserFavorite: !video.isUserFavorite,
              })
            );
          },
        };
      }

      case UNSTAR: {
        if (
          !checkConditionsMet([
            userInSameWorkspace,
            video.isUserFavorite,
            scopes.hasFavoritesScope,
            scopes.hasAuthenticatedAccess,
            !flags.destinationRolloutHubFlag,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(UNSTAR),
          onClick: () => {
            dispatch(toggleVideoFavoriteCreator(video.id, false, video));
            dispatch(
              updateShareVideo({
                ...video,
                isUserFavorite: !video.isUserFavorite,
              })
            );
          },
        };
      }

      case PIN_TO_PROFILE: {
        if (
          !checkConditionsMet([
            flags.destinationRolloutHubFlag,
            !video.isPinned,
            userIsOwner,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(PIN_TO_PROFILE),
          disabled: video.visibility !== VisibilityType.WORKSPACE,
          onClick: async () => {
            await dispatch(
              updateUserPinnedVideo(video, 1, userInSameWorkspace)
            );
          },
          title:
            video.visibility !== VisibilityType.WORKSPACE ? (
              <Tooltip
                content={PIN_HELPER_TEXT}
                placement="rightCenter"
                triggerOffset={16}
              >
                {PIN_TO_PROFILE}
              </Tooltip>
            ) : (
              PIN_TO_PROFILE
            ),
        };
      }

      case UNPIN_FROM_PROFILE: {
        if (
          !checkConditionsMet([
            flags.destinationRolloutHubFlag,
            video.isPinned,
            userIsOwner,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(UNPIN_FROM_PROFILE),
          disabled: video.visibility !== VisibilityType.WORKSPACE,
          onClick: async () => {
            await dispatch(
              updateUserPinnedVideo(video, 0, userInSameWorkspace)
            );
          },
        };
      }

      case DUPLICATE: {
        if (
          !checkConditionsMet([
            userIsOwner || (video.is_team_shared && userInSameWorkspace),
            !video.archived,
            scopes.hasVideoToolsAccessScope,
            scopes.hasAuthenticatedAccess,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(DUPLICATE),
          onClick: () => {
            const newName = `Copy of ${video.name}`;

            dispatch(duplicateVideoCreator(video.id, newName)).then(
              newVideo => {
                const url = `https://${LOOM_BARE_URI}/share/${newVideo.id}`;

                copy(url, { format: 'text/plain' });
                setTimeout(() => {
                  window.open(url, '_blank');
                }, 1000);
              }
            );

            analytics.track(DUPLICATE_ITEM_CLICK, {
              click_source: SHARE_PAGE,
              entity_id: video.id,
              entity_type: 'video',
              workspace: 'private',
            });
          },
        };
      }

      case ARCHIVE: {
        if (
          !checkConditionsMet([
            userIsOwner || (video.is_team_shared && userInSameWorkspace),
            !video.archived,
            scopes.hasArchiveAccessScope,
            scopes.hasAuthenticatedAccess,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(ARCHIVE),
          onClick: () => {
            dispatch(archiveVideoCreator(video.id)).then(() => {
              const url = `https://${LOOM_BARE_URI}${MY_VIDEOS_PAGE}`;

              window.location.replace(url);
            });
          },
        };
      }

      case UNARCHIVE: {
        if (
          !checkConditionsMet([
            userIsOwner || (video.is_team_shared && userInSameWorkspace),
            video.archived,
            scopes.hasArchiveAccessScope,
            scopes.hasAuthenticatedAccess,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(UNARCHIVE),
          onClick: () => {
            video.type = 'video';
            dispatch(updateShowMoveModal(true, [video]));
          },
        };
      }

      case DOWNLOAD: {
        if (
          !checkConditionsMet([
            userCanEdit || video.download_enabled,
            !video.needs_password,
            !video.archived,
          ])
        ) {
          return null;
        }

        const downloadUrl = extras.downloadUrl;

        return {
          ...commonProps(DOWNLOAD),
          disabled: !downloadUrl,
          onClick: () => {
            const downloadName = `${video.name || video.id}.mp4`;
            const downloadParams = {
              url: downloadUrl,
              name: downloadName,
              opts: {
                video_id: video.id,
                is_owner: video.current_user_is_owner,
              },
            };

            if (extras.shouldShowDownloadInfoModal) {
              dispatch(openModal(DOWNLOAD_VIDEO_INFO_MODAL, downloadParams));
              dispatch(completeTriggerCreator(SHOW_DOWNLOAD_VIDEO_INFO_MODAL));
            } else {
              dispatch(startDownload(downloadParams));
            }
          },
          title:
            !downloadUrl && video?.processing_information?.trim_id ? (
              <TrimProgressTooltip
                videoId={video.id}
                onComplete={dispatch(fetchShareVideoDownloadUrlCreator())}
              >
                {DOWNLOAD}
              </TrimProgressTooltip>
            ) : (
              DOWNLOAD
            ),
        };
      }

      case DELETE: {
        if (
          !checkConditionsMet([
            userIsOwner || (video.is_team_shared && userInSameWorkspace),
            scopes.hasVideoToolsAccessScope,
            scopes.hasAuthenticatedAccess,
          ])
        ) {
          return null;
        }

        return {
          ...commonProps(DELETE),
          onClick: () => {
            dispatch(
              openModal(CONFIRM_DELETE_ITEM_MODAL, {
                videos: [video],
                redirectUrl: `https://${LOOM_BARE_URI}${MY_VIDEOS_PAGE}`,
              })
            );
          },
        };
      }

      case DIVE: {
        if (userIsLoomAdmin || isDev) {
          return {
            ...commonProps(DIVE),
            onClick: () => {
              const url = `https://${LOOM_BARE_URI}/admin/video-dive/${video.id}`;

              window.location.replace(url);
            },
          };
        }

        return null;
      }

      default:
        return null;
    }
  };

  return SHARE_VIDEO_MENU.map(o => getVideoOptionOrNull(o)).filter(i => i);
};

export default ({ video }) => {
  const hasTeamLibraryWriteScope = useHasScope(TEAM_LIBRARY_WRITE);
  const hasVideoToolsAccessScope = useHasScope(VIDEO_TOOLS_ACCESS);
  const hasArchiveAccessScope = useHasScope(PERSONAL_ARCHIVE_WRITE);

  const scopes = {
    hasSharedLibraryWriteScope: useHasScope(SHARED_LIBRARY_WRITE),
    hasVideoSharePrivateScope: useHasScope(VIDEO_SHARE_PRIVATE_ACTION),
    hasPersonalLibraryWriteScope: useHasScope(PERSONAL_LIBRARY_WRITE),
    hasTeamLibraryWriteScope,
    hasVideoToolsAccessScope,
    hasArchiveAccessScope,
    hasAuthenticatedAccess: useHasScope(AUTHENTICATED_USER_ACCESS),
    hasFavoritesScope: useHasScope(FAVORITES),
  };

  // Flags
  const flags = {
    isInFillerWordRemovalFlag: useFlagIsActivated({
      flag: STORE_FILLER_WORD_RANGES,
      activationValues: [true],
      skipLoggingToSegment: false,
    }),
    destinationRolloutHubFlag: useDestination(),
  };

  // Permissions
  let permissions = {};
  const userInSameWorkspace = useUserInSameWorkspaceAsItem(video);

  const userCanAccessTranscript = useSelector(
    state => state.video_player?.userCanAccessTranscript
  );

  const userIsLoomAdmin = useSelector(
    state => state.user?.current?.role === ADMIN
  );

  permissions = {
    userIsOwner: video.current_user_is_owner,
    userCanEdit: video.currentUserCanEdit,
    userInSameWorkspace,
    userCanAccessTranscript,
    userIsLoomAdmin,
  };

  // Extras
  const extras = {
    shouldShowDownloadInfoModal: useShouldShowTrigger(
      SHOW_DOWNLOAD_VIDEO_INFO_MODAL
    ),
    isTrimMode: useSelector(state => state.trimming?.in_trim_mode),
    transcriptStatus: useSelector(
      state => state.share_video?.transcript_details?.transcription_status
    ),
    downloadUrl: useSelector(state => state.share_video?.download_url),
    videoButtonsShowing: useMatchMedia(
      `(min-width: ${LARGE_TABLET_MIN_WIDTH}px)`
    ),
  };

  // Menu construction
  const dispatch = useDispatch();

  return getVideoActionMenuItems({
    video,
    scopes,
    flags,
    permissions,
    dispatch,
    extras,
  });
};
